import { useEffect as useEffectSv, useRef as useRefSv, useState as useStateSv } from "react";
import { Api } from "./api";
import { Banner, Field, Icon } from "./components";

// Community Services — neighbors offering services to neighbors.
// 3 ACTIVE per user per community. 30-day auto-expire.
// Mirrors marketplace.jsx structure (browse, detail, form).

const SV_CATEGORY_LABEL = {
  childcare: "Childcare / Babysitting",
  petcare: "Pet sitting / Walking",
  handyman: "Handyman",
  lawn: "Lawn / Yard",
  cleaning: "Cleaning",
  tutoring: "Tutoring",
  tech_help: "Tech help",
  transport: "Rides / Transport",
  other: "Other",
};
const SV_CATEGORY_ICON = {
  childcare: "users", petcare: "spark", handyman: "settings",
  lawn: "spark", cleaning: "spark", tutoring: "book",
  tech_help: "settings", transport: "arrow", other: "tag",
};
const SV_RATE_KIND_LABEL = {
  hourly: "Hourly",
  flat: "Flat rate",
  by_quote: "By quote",
};
const SV_STATUS_PILL = {
  active: { text: "Available", cls: "pill--green" },
  paused: { text: "Paused", cls: "pill--gold" },
  withdrawn: { text: "Withdrawn", cls: "pill--ghost" },
  expired: { text: "Expired", cls: "pill--ghost" },
};

function _fmtRateSv(o) {
  if (o.rate_kind === "by_quote") return "By quote";
  if (o.rate_cents == null) return "Ask provider";
  const d = o.rate_cents / 100;
  const dollars = "$" + d.toLocaleString(undefined, { minimumFractionDigits: d % 1 ? 2 : 0, maximumFractionDigits: 2 });
  return o.rate_kind === "hourly" ? `${dollars}/hr` : dollars;
}

function _relTimeSv(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ─── Stars ─────────────────────────────────────────────────────────────
   A compact inline star display + an interactive star-picker for the
   review form. Five U+2605 / U+2606 glyphs is the simplest thing that
   reads clearly at small sizes and scales without an icon set. */
function StarRow({ value, size = 14, color }) {
  // value can be fractional (e.g. 4.7). We fill whole stars solidly,
  // render a half star with a clip-path for the partial position, and
  // leave the rest hollow.
  const stars = [];
  const fill = color || "var(--accent, #c25e3a)";
  for (let i = 1; i <= 5; i++) {
    const frac = Math.max(0, Math.min(1, (value || 0) - (i - 1)));
    stars.push(
      <span key={i} style={{ position: "relative", display: "inline-block", width: size + 2, lineHeight: 1, fontSize: size, color: "var(--hairline)" }}>
        <span aria-hidden>★</span>
        {frac > 0 && (
          <span aria-hidden style={{
            position: "absolute", top: 0, left: 0, overflow: "hidden",
            width: `${frac * 100}%`, color: fill,
          }}>★</span>
        )}
      </span>,
    );
  }
  return <span className="row" style={{ gap: 0, lineHeight: 1 }} aria-label={`${(value || 0).toFixed(1)} out of 5 stars`}>{stars}</span>;
}

function RatingSummary({ summary, size = 13, mutedCount = true }) {
  if (!summary || !summary.count) {
    return <span style={{ fontSize: size, color: "var(--mute)" }}>No reviews yet</span>;
  }
  return (
    <span className="row" style={{ gap: 6, alignItems: "center" }}>
      <StarRow value={summary.average || 0} size={size + 1} />
      <span style={{ fontSize: size, fontWeight: 600 }}>{(summary.average || 0).toFixed(1)}</span>
      <span style={{ fontSize: size - 1, color: mutedCount ? "var(--mute)" : "inherit" }}>
        ({summary.count})
      </span>
    </span>
  );
}

function StarPicker({ value, onChange, disabled }) {
  // Interactive 5-star input. Click to set. Keyboard: arrows nudge.
  const [hover, setHover] = useStateSv(0);
  const v = hover || value || 0;
  return (
    <div
      className="row"
      role="radiogroup"
      aria-label="Rating"
      style={{ gap: 2, alignItems: "center" }}
      onMouseLeave={() => setHover(0)}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); onChange(Math.max(1, (value || 1) - 1)); }
        if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); onChange(Math.min(5, (value || 0) + 1)); }
      }}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          role="radio"
          aria-checked={value === i}
          disabled={disabled}
          onMouseEnter={() => setHover(i)}
          onClick={() => onChange(i)}
          style={{
            background: "transparent", border: 0, cursor: disabled ? "default" : "pointer",
            fontSize: 26, lineHeight: 1, padding: "2px 4px",
            color: i <= v ? "var(--accent, #c25e3a)" : "var(--hairline)",
            transition: "transform 80ms ease",
          }}
        >
          ★
        </button>
      ))}
      {value > 0 && !disabled && (
        <button type="button" onClick={() => onChange(0)}
          style={{ marginLeft: 6, background: "transparent", border: 0, color: "var(--mute)", cursor: "pointer", fontSize: 12 }}>
          clear
        </button>
      )}
    </div>
  );
}

/* ─── ServiceImage — routes through the shared Api.authedBlobUrl cache. ── */
// CODE_REVIEW P2 L266: same pattern as marketplace's AuthedImage.
function ServiceImage({ imageId, alt = "", style }) {
  const [src, setSrc] = useStateSv(null);
  const [err, setErr] = useStateSv(false);
  useEffectSv(() => {
    if (!imageId) { setSrc(null); setErr(false); return; }
    let cancelled = false;
    let release = null;
    setErr(false);
    window.Api.authedBlobUrl(`/api/services/images/${imageId}`).then(
      ({ url, release: r }) => {
        if (cancelled) { r(); return; }
        release = r;
        setSrc(url);
      },
      () => { if (!cancelled) setErr(true); },
    );
    return () => { cancelled = true; if (release) release(); };
  }, [imageId]);
  if (err || !src) return null;
  return <img src={src} alt={alt} style={style} />;
}

function Services({ user, onMessageProvider }) {
  const [view, setView] = useStateSv("browse");
  const [activeId, setActiveId] = useStateSv(null);
  const open = (id) => { setActiveId(id); setView("detail"); };
  const openNew = () => { setActiveId(null); setView("new"); };
  const openEdit = (id) => { setActiveId(id); setView("edit"); };
  const back = () => { setView("browse"); setActiveId(null); };

  if (view === "detail" && activeId) {
    return <ServiceDetail id={activeId} user={user} onBack={back}
      onEdit={() => openEdit(activeId)} onDeleted={back}
      onMessageProvider={onMessageProvider} />;
  }
  if (view === "new") return <ServiceForm user={user} onCancel={back} onSaved={(o) => open(o.id)} />;
  if (view === "edit" && activeId) {
    return <ServiceForm user={user} editingId={activeId}
      onCancel={() => open(activeId)} onSaved={() => open(activeId)} />;
  }
  return <ServiceBrowse user={user} onOpen={open} onNew={openNew} />;
}

function ServiceBrowse({ user, onOpen, onNew }) {
  const [state, setState] = useStateSv({ loading: true, error: null, offers: [], meta: null });
  const [usage, setUsage] = useStateSv(null);
  const [category, setCategory] = useStateSv("");
  const [mineOnly, setMineOnly] = useStateSv(false);
  const [hideClosed, setHideClosed] = useStateSv(true);
  const [q, setQ] = useStateSv("");
  // {limit, offset, total, has_more} pagination — CODE_REVIEW P2 L268.
  const [offset, setOffset] = useStateSv(0);
  // Request-invalidation counter — see marketplace.jsx for context
  // (codex P1 on PR #156). Filter-change + offset-reset can race; only
  // the latest reload's response wins.
  const reqIdRef = useRefSv(0);

  const reload = (nextOffset = offset) => {
    const myReq = ++reqIdRef.current;
    setState((s) => ({ loading: true, error: null, offers: [], meta: s.meta }));
    window.Api.servicesList({
      category: category || undefined,
      mine_only: mineOnly,
      status: hideClosed ? "active,paused" : "active,paused,withdrawn,expired",
      q: q || undefined,
      offset: nextOffset,
    })
      .then(({ items, meta }) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: null, offers: items || [], meta: meta || null });
      })
      .catch((err) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: err.message || "Couldn't load.", offers: [], meta: null });
      });
    window.Api.servicesUsage().then(setUsage).catch(() => {});
  };
  useEffectSv(() => { setOffset(0); /* eslint-disable-next-line */ }, [category, mineOnly, hideClosed]);
  useEffectSv(() => { reload(offset); /* eslint-disable-next-line */ }, [category, mineOnly, hideClosed, offset]);
  useEffectSv(() => {
    const t = setTimeout(() => { setOffset(0); reload(0); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const community = user?.community?.name || "your community";

  return (
    <div className="scroll">
      <div className="stack-md">
        <Banner icon="users" tone="accent">
          <b>{community} Services.</b> Neighbors offering services to neighbors —
          babysitting, pet sitting, handyman, lawn care, tutoring. Three active
          offers per person, so what you see is real and current.
        </Banner>

        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div className="card" style={{ padding: "8px 12px", flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 260 }}>
            <Icon name="search" size={16} style={{ color: "var(--mute)" }} />
            <input className="input" aria-label="Search services" placeholder="Search services…" value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ border: 0, padding: 0, background: "transparent", width: "100%" }} />
          </div>
          <button className="btn btn--primary" onClick={onNew}
            disabled={usage && usage.active >= usage.active_limit}
            title={usage && usage.active >= usage.active_limit
              ? "You're at the 3-offer cap — pause or withdraw one first." : undefined}>
            <Icon name="plus" size={16} /> &nbsp;Offer a service
          </button>
        </div>

        {usage && <ServiceUsageBadge usage={usage} />}

        <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
          <CategoryChipSv active={!category} onClick={() => setCategory("")} label="All" iconName="tag" />
          {Object.keys(SV_CATEGORY_LABEL).map((k) => (
            <CategoryChipSv key={k} active={category === k} onClick={() => setCategory(k)}
              label={SV_CATEGORY_LABEL[k]} iconName={SV_CATEGORY_ICON[k]} />
          ))}
        </div>

        <div className="row" style={{ gap: 16, fontSize: 13, color: "var(--mute)" }}>
          <label className="row" style={{ gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
            Only my offers
          </label>
          <label className="row" style={{ gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={hideClosed} onChange={(e) => setHideClosed(e.target.checked)} />
            Hide withdrawn / expired
          </label>
        </div>

        {state.loading && (
          <div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading services…</div>
        )}
        {state.error && (
          <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{state.error}</div>
        )}
        {!state.loading && !state.error && state.offers.length === 0 && (
          <div className="card card--pad-lg" style={{ textAlign: "center" }}>
            <div style={{ color: "var(--mute)", marginBottom: 14 }}>
              {q || category || mineOnly
                ? "No services match your filters."
                : "No services in your community yet. Be the first to offer one!"}
            </div>
            <button className="btn btn--primary" onClick={onNew}>Offer the first service</button>
          </div>
        )}
        {!state.loading && state.offers.length > 0 && (
          <div className="grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
            {state.offers.map((o) => <ServiceCard key={o.id} offer={o} onClick={() => onOpen(o.id)} />)}
          </div>
        )}
        <window.Pager meta={state.meta} offset={offset} onChange={setOffset} busy={state.loading} />
      </div>
    </div>
  );
}

function ServiceUsageBadge({ usage }) {
  const pct = (usage.active / usage.active_limit) * 100;
  const nearCap = usage.active >= usage.active_limit;
  const warn = nearCap || pct >= 67;
  return (
    <div className="card" style={{
      padding: "10px 14px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16,
      background: warn ? "var(--accent-soft, #eef3e6)" : "var(--surface)",
      border: warn ? "1px solid color-mix(in oklab, var(--accent, #7c8a6b) 22%, transparent)" : "1px solid var(--hairline)",
      fontSize: 13,
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200, flex: 1 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span style={{ fontWeight: 500 }}>Active offers</span>
          <span className="mono" style={{ color: nearCap ? "var(--danger)" : "var(--mute)" }}>
            {usage.active} / {usage.active_limit}
          </span>
        </div>
        <div style={{ height: 4, background: "var(--bg-2)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: nearCap ? "var(--danger)" : "var(--accent)" }} />
        </div>
      </div>
      {warn && (
        <div style={{ fontSize: 12, color: "var(--mute)", flexBasis: "100%" }}>
          {nearCap
            ? "You're at the cap — pause or withdraw an offer to post a new one."
            : "Offers auto-expire after 30 days. Keep them fresh!"}
        </div>
      )}
    </div>
  );
}

function CategoryChipSv({ active, onClick, label, iconName }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 12px", borderRadius: 999,
      border: active ? "1px solid var(--accent)" : "1px solid var(--hairline)",
      background: active ? "var(--accent-soft)" : "var(--surface)",
      color: active ? "var(--accent-ink)" : "var(--ink)",
      fontSize: 13, fontWeight: active ? 500 : 400, cursor: "pointer",
    }}>
      <Icon name={iconName || "tag"} size={13} style={{ opacity: 0.7 }} />
      {label}
    </button>
  );
}

function ServiceCard({ offer, onClick }) {
  const pill = SV_STATUS_PILL[offer.status] || SV_STATUS_PILL.active;
  const firstImage = (offer.images || [])[0];
  return (
    <button type="button" onClick={onClick} style={{
      display: "flex", flexDirection: "column", background: "var(--surface)",
      border: "1px solid var(--hairline)", borderRadius: 12, overflow: "hidden",
      cursor: "pointer", textAlign: "left", padding: 0,
      transition: "transform 80ms ease, box-shadow 80ms ease",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-lg)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; e.currentTarget.style.transform = ""; }}>
      <div style={{ aspectRatio: "4/3", position: "relative", background: "var(--bg-2)" }}>
        {firstImage ? (
          <ServiceImage imageId={firstImage.id} alt={offer.title}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: "var(--mute)" }}>
            <Icon name={SV_CATEGORY_ICON[offer.category] || "users"} size={32} />
          </div>
        )}
        {offer.status !== "active" && (
          <span className={`pill ${pill.cls}`} style={{ position: "absolute", top: 8, left: 8 }}>{pill.text}</span>
        )}
        {offer.is_mine && (
          <span className="pill pill--accent" style={{ position: "absolute", top: 8, right: 8 }}>Your post</span>
        )}
      </div>
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
          {offer.title}
        </div>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{_fmtRateSv(offer)}</span>
          <span className="text-xs text-mute">{_relTimeSv(offer.created_at)}</span>
        </div>
        <div className="row" style={{ gap: 4, fontSize: 11, color: "var(--mute)" }}>
          <span>{SV_CATEGORY_LABEL[offer.category] || offer.category}</span>
          <span>·</span>
          <span>{offer.provider?.display_name || offer.provider?.full_name || "Neighbor"}</span>
        </div>
        {/* Provider's overall rating across all their offers in this
            community. Hidden when no reviews exist so new providers
            don't show a sad "no reviews" line on every card. */}
        {offer.provider_rating && offer.provider_rating.count > 0 && (
          <div style={{ marginTop: 2 }}>
            <RatingSummary summary={offer.provider_rating} size={12} />
          </div>
        )}
      </div>
    </button>
  );
}

function ServiceDetail({ id, user, onBack, onEdit, onDeleted, onMessageProvider }) {
  const [state, setState] = useStateSv({ loading: true, error: null, offer: null });
  const [busy, setBusy] = useStateSv(false);
  const [activeImage, setActiveImage] = useStateSv(0);

  const reload = () => {
    setState({ loading: true, error: null, offer: null });
    window.Api.servicesGet(id)
      .then((offer) => setState({ loading: false, error: null, offer }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load.", offer: null }));
  };
  useEffectSv(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const updated = await window.Api.servicesUpdate(id, { status });
      setState({ loading: false, error: null, offer: updated });
    } catch (err) { alert(err.message || "Couldn't update."); }
    finally { setBusy(false); }
  };
  const onDelete = async () => {
    if (!window.confirm("Delete this offer? This cannot be undone.")) return;
    setBusy(true);
    try { await window.Api.servicesDelete(id); onDeleted(); }
    catch (err) { alert(err.message || "Couldn't delete."); setBusy(false); }
  };

  if (state.loading) return <div className="scroll"><div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading…</div></div>;
  if (state.error || !state.offer) return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{state.error || "Not found."}</div></div>;

  const o = state.offer;
  const pill = SV_STATUS_PILL[o.status] || SV_STATUS_PILL.active;
  const provider = o.provider;
  const name = (provider.display_name || provider.full_name || "Neighbor").trim();

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 980 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>
            <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> &nbsp;Back to services
          </button>
          {o.can_edit && (
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn--ghost btn--sm" onClick={onEdit} disabled={busy}>
                <Icon name="edit" size={14} /> &nbsp;Edit
              </button>
              <button className="btn btn--ghost btn--sm" onClick={onDelete} disabled={busy} style={{ color: "var(--danger)" }}>
                <Icon name="trash" size={14} /> &nbsp;Delete
              </button>
            </div>
          )}
        </div>

        <div className="grid" style={{ display: "grid", gridTemplateColumns: o.images.length > 0 ? "minmax(0, 1.4fr) minmax(280px, 1fr)" : "1fr", gap: 24 }}>
          {o.images.length > 0 && (
            <div>
              <div style={{ aspectRatio: "4/3", background: "var(--bg-2)", borderRadius: 12, overflow: "hidden", position: "relative" }}>
                <ServiceImage imageId={o.images[activeImage]?.id} alt={o.title}
                  style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                {o.status !== "active" && (
                  <span className={`pill ${pill.cls}`} style={{ position: "absolute", top: 12, left: 12 }}>{pill.text}</span>
                )}
              </div>
              {o.images.length > 1 && (
                <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  {o.images.map((img, i) => (
                    <button key={img.id} type="button" onClick={() => setActiveImage(i)}
                      style={{ width: 64, height: 64, padding: 0, borderRadius: 8, overflow: "hidden",
                        border: i === activeImage ? "2px solid var(--accent)" : "1px solid var(--hairline)",
                        cursor: "pointer", background: "var(--bg-2)" }}>
                      <ServiceImage imageId={img.id} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="col" style={{ gap: 16 }}>
            <div>
              <div className="text-sm text-mute" style={{ marginBottom: 4 }}>
                {SV_CATEGORY_LABEL[o.category] || o.category}
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0, lineHeight: 1.2 }}>{o.title}</h1>
              <div style={{ fontSize: 22, fontWeight: 600, marginTop: 8 }}>{_fmtRateSv(o)}</div>
              <div className="row" style={{ gap: 6, marginTop: 6, fontSize: 13, color: "var(--mute)" }}>
                <span>{SV_RATE_KIND_LABEL[o.rate_kind] || o.rate_kind}</span>
                <span>·</span>
                <span>Posted {_relTimeSv(o.created_at)}</span>
              </div>
            </div>

            {o.description && (
              <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.55 }}>{o.description}</div>
            )}

            <div className="card" style={{ padding: 14 }}>
              <div className="text-xs text-mute" style={{ marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>Provider</div>
              <div style={{ fontWeight: 600 }}>{name}</div>
              {/* Provider's overall stars across all their offers in
                  this community. Sits right under their name so a
                  potential client can scan the rep before reading. */}
              <div style={{ marginTop: 6 }}>
                <RatingSummary summary={o.provider_rating} size={13} />
              </div>
              {!o.is_mine && o.status === "active" && (
                <button type="button" className="btn btn--primary btn--block" style={{ marginTop: 10 }}
                  onClick={() => onMessageProvider && onMessageProvider({
                    recipient_user_id: provider.id,
                    recipient_name: name,
                    related_service_id: o.id,
                    listing_title: o.title,
                  })}
                  disabled={!onMessageProvider}>
                  Message {name.split(" ")[0]}
                </button>
              )}
            </div>

            {o.is_mine && (
              <div className="card" style={{ padding: 14 }}>
                <div className="text-xs text-mute" style={{ marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>Status</div>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  {o.status !== "active" && (
                    <button className="btn btn--ghost btn--sm" onClick={() => setStatus("active")} disabled={busy}>Resume</button>
                  )}
                  {o.status === "active" && (
                    <button className="btn btn--ghost btn--sm" onClick={() => setStatus("paused")} disabled={busy}>Pause</button>
                  )}
                  {o.status !== "withdrawn" && (
                    <button className="btn btn--ghost btn--sm" onClick={() => setStatus("withdrawn")} disabled={busy}>Withdraw</button>
                  )}
                </div>
              </div>
            )}

            {!o.is_mine && o.can_moderate && (
              <div className="card" style={{ padding: 14 }}>
                <div className="text-xs text-mute" style={{ marginBottom: 6 }}>Moderator</div>
                <button className="btn btn--ghost btn--sm" onClick={onDelete} style={{ color: "var(--danger)" }} disabled={busy}>
                  Remove this offer
                </button>
              </div>
            )}
          </div>
        </div>

        <ReviewsSection offerId={o.id} providerName={name} />
      </div>
    </div>
  );
}

/* ─── Reviews section ──────────────────────────────────────────────────
   Lives on the service detail page. Shows the per-offer star summary,
   the list of reviews, and (if the viewer is eligible) a write/edit
   form. Re-fetches its own data instead of relying on the parent so
   submitting a review updates the stars immediately. */
function ReviewsSection({ offerId, providerName }) {
  const [state, setState] = useStateSv({ loading: true, error: null, data: null });
  const [composing, setComposing] = useStateSv(false);
  const [editingId, setEditingId] = useStateSv(null);

  const reload = () => {
    setState({ loading: true, error: null, data: null });
    window.Api.servicesOfferReviews(offerId)
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load reviews.", data: null }));
  };
  useEffectSv(() => { reload(); /* eslint-disable-next-line */ }, [offerId]);

  if (state.loading) {
    return <div className="card" style={{ padding: 14, color: "var(--mute)" }}>Loading reviews…</div>;
  }
  if (state.error || !state.data) {
    return <div className="card" style={{ padding: 14, color: "var(--danger)" }}>{state.error || "Couldn't load reviews."}</div>;
  }

  const d = state.data;
  const summary = d.summary || { average: null, count: 0 };
  const reviews = d.reviews || [];
  const cap = d.viewer_can_review || { can_review: false, reason: null, existing_review_id: null };
  const mine = reviews.find((r) => r.is_mine);
  const showForm = composing || editingId !== null;

  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Reviews of this offer</div>
          <RatingSummary summary={summary} size={14} />
        </div>
        {cap.can_review && !showForm && (
          mine
            ? <button className="btn btn--ghost btn--sm" onClick={() => setEditingId(mine.id)}>Edit your review</button>
            : <button className="btn btn--primary btn--sm" onClick={() => setComposing(true)}>Write a review</button>
        )}
      </div>

      {!cap.can_review && cap.reason === "no_contact" && (
        <div className="text-sm text-mute" style={{ marginTop: 10 }}>
          Send {providerName.split(" ")[0]} a message first — we only allow reviews from neighbors
          who've actually been in touch.
        </div>
      )}
      {!cap.can_review && cap.reason === "self" && (
        <div className="text-sm text-mute" style={{ marginTop: 10 }}>
          You can't review your own offer.
        </div>
      )}

      {showForm && (
        <div style={{ marginTop: 14 }}>
          <ReviewForm
            offerId={offerId}
            editing={editingId ? reviews.find((r) => r.id === editingId) : null}
            onCancel={() => { setComposing(false); setEditingId(null); }}
            onSaved={() => { setComposing(false); setEditingId(null); reload(); }}
            onDeleted={() => { setComposing(false); setEditingId(null); reload(); }}
          />
        </div>
      )}

      {reviews.length === 0 && !showForm && (
        <div className="text-sm text-mute" style={{ marginTop: 14 }}>
          No reviews yet. Be the first neighbor to leave one.
        </div>
      )}
      {reviews.length > 0 && (
        <div className="stack-sm" style={{ marginTop: 14 }}>
          {reviews.map((r) => (
            <ReviewItem key={r.id} review={r} onEdit={() => setEditingId(r.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewItem({ review, onEdit }) {
  const reviewer = review.reviewer || {};
  const name = (reviewer.display_name || reviewer.full_name || "Neighbor").trim();
  return (
    <div className="card" style={{ padding: 12, border: "1px solid var(--hairline)" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <StarRow value={review.rating} size={13} />
          <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>
          <span className="text-xs text-mute">{_relTimeSv(review.created_at)}</span>
          {review.updated_at && review.updated_at !== review.created_at && (
            <span className="text-xs text-mute">· edited</span>
          )}
        </div>
        {review.is_mine && (
          <button className="btn btn--ghost btn--sm" onClick={onEdit}>Edit</button>
        )}
      </div>
      {review.body && (
        <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.55, marginTop: 8 }}>{review.body}</div>
      )}
    </div>
  );
}

function ReviewForm({ offerId, editing, onCancel, onSaved, onDeleted }) {
  const [rating, setRating] = useStateSv(editing?.rating || 0);
  const [body, setBody] = useStateSv(editing?.body || "");
  const [busy, setBusy] = useStateSv(false);
  const [error, setError] = useStateSv(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError(null);
    if (!rating || rating < 1 || rating > 5) {
      setError("Pick a rating from 1 to 5 stars.");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await window.Api.servicesReviewUpdate(editing.id, { rating, body: body || null });
      } else {
        await window.Api.servicesReviewCreate(offerId, { rating, body: body || null });
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Couldn't save your review.");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!editing) return;
    if (!window.confirm("Delete your review? This cannot be undone.")) return;
    setBusy(true);
    try {
      await window.Api.servicesReviewDelete(editing.id);
      onDeleted();
    } catch (err) {
      setError(err.message || "Couldn't delete.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="stack-sm" style={{ borderTop: "1px solid var(--hairline)", paddingTop: 14 }}>
      <div style={{ fontWeight: 600 }}>{editing ? "Edit your review" : "Leave a review"}</div>
      {error && (
        <div role="alert" style={{ padding: "8px 10px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}>
          {error}
        </div>
      )}
      <StarPicker value={rating} onChange={setRating} disabled={busy} />
      <textarea
        className="input"
        rows={4}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="How was the experience? (Optional — but it helps your neighbors.)"
        maxLength={4000}
        style={{ resize: "vertical", lineHeight: 1.5 }}
      />
      <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
        <div>
          {editing && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onDelete} disabled={busy} style={{ color: "var(--danger)" }}>
              Delete review
            </button>
          )}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
            {busy ? "Saving…" : (editing ? "Save changes" : "Post review")}
          </button>
        </div>
      </div>
    </form>
  );
}

const SV_MAX_IMAGES = 3;

function ServiceForm({ user, editingId, onCancel, onSaved }) {
  const isEdit = !!editingId;
  const [loaded, setLoaded] = useStateSv(!isEdit);
  const [title, setTitle] = useStateSv("");
  const [description, setDescription] = useStateSv("");
  const [category, setCategory] = useStateSv("other");
  const [rateKind, setRateKind] = useStateSv("hourly");
  const [rate, setRate] = useStateSv("");
  const [images, setImages] = useStateSv([]);
  const [pendingFiles, setPendingFiles] = useStateSv([]);
  const [busy, setBusy] = useStateSv(false);
  const [error, setError] = useStateSv(null);
  const fileInputRef = useRefSv(null);

  // Revoke any blob URLs still attached when the form unmounts
  // (route change / cancel). Ref dance so cleanup sees the latest list
  // without re-running on every change (which would revoke URLs that
  // are still in use).
  const pendingFilesRef = useRefSv(pendingFiles);
  useEffectSv(() => { pendingFilesRef.current = pendingFiles; }, [pendingFiles]);
  useEffectSv(() => {
    return () => {
      pendingFilesRef.current.forEach((f) => {
        if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  useEffectSv(() => {
    if (!isEdit) return;
    window.Api.servicesGet(editingId).then((o) => {
      setTitle(o.title);
      setDescription(o.description || "");
      setCategory(o.category);
      setRateKind(o.rate_kind);
      setRate(o.rate_cents != null ? (o.rate_cents / 100).toString() : "");
      setImages(o.images || []);
      setLoaded(true);
    }).catch((err) => setError(err.message || "Couldn't load."));
  }, [editingId, isEdit]);

  // Wrap each picked File with a stable id + cached blob URL — see
  // marketplace.jsx for the same fix (codex P1 on #244): inline
  // URL.createObjectURL(f) on every render leaked blob URLs per
  // keystroke and index-keyed nodes showed the wrong photo after
  // removing a non-last entry.
  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const room = SV_MAX_IMAGES - images.length - pendingFiles.length;
    if (room <= 0) { setError(`Max ${SV_MAX_IMAGES} images per offer.`); return; }
    const wrapped = files.slice(0, room).map((file) => ({
      id: (window.crypto?.randomUUID?.()) || `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingFiles((cur) => [...cur, ...wrapped]);
    e.target.value = "";
  };
  const onRemovePending = (id) => setPendingFiles((cur) => {
    const removed = cur.find((f) => f.id === id);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    return cur.filter((f) => f.id !== id);
  });
  const onRemoveExisting = async (imageId) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      await window.Api.servicesDeleteImage(imageId);
      setImages((cur) => cur.filter((i) => i.id !== imageId));
    } catch (err) { setError(err.message || "Couldn't remove image."); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError(null);
    if (!title.trim()) { setError("Title is required."); return; }
    if (rateKind !== "by_quote" && rate && isNaN(parseFloat(rate.replace(/[$,]/g, "")))) {
      setError("Rate must be a number, or choose 'By quote'."); return;
    }
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        category,
        rate_kind: rateKind,
        rate: rateKind === "by_quote" ? null : (rate.trim() || null),
      };
      const saved = isEdit
        ? await window.Api.servicesUpdate(editingId, body)
        : await window.Api.servicesCreate(body);
      // Track failed uploads so the user gets a heads-up — pre-#251 we
      // silently dropped photos on flaky networks with no indication.
      const failedUploads = [];
      for (const wrapped of pendingFiles) {
        try { await window.Api.servicesUploadImage(saved.id, wrapped.file); }
        catch (uploadErr) {
          console.warn("image upload failed:", uploadErr);
          failedUploads.push(wrapped);
        }
      }
      if (failedUploads.length > 0) {
        window.alert(
          `Saved, but ${failedUploads.length} photo${failedUploads.length === 1 ? "" : "s"} couldn't upload (likely a flaky connection). Open the offer to add ${failedUploads.length === 1 ? "it" : "them"} again.`,
        );
      }
      onSaved(saved);
    } catch (err) { setError(err.message || "Couldn't save."); }
    finally { setBusy(false); }
  };

  if (isEdit && !loaded) {
    return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading…</div></div>;
  }

  return (
    <div className="scroll">
      <form className="stack-md" onSubmit={submit} style={{ maxWidth: 700 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{isEdit ? "Edit offer" : "Offer a service"}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>Cancel</button>
        </div>

        {error && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}>
            {error}
          </div>
        )}

        <div className="card card--pad-lg" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="What are you offering?" hint="Keep it short and specific.">
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Babysitting weekday evenings" maxLength={140} />
          </Field>

          <Field label="Details" hint="Schedule, age groups, what's included, anything neighbors should know.">
            <textarea className="input" rows={5} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Available M–F 5–10pm. Comfortable with infants through 10 y/o. CPR certified. Reach out anytime!"
              style={{ resize: "vertical", lineHeight: 1.5 }} />
          </Field>

          <div className="auth__form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Category">
              <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(SV_CATEGORY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <Field label="Rate type">
              <select className="select" value={rateKind} onChange={(e) => setRateKind(e.target.value)}>
                {Object.entries(SV_RATE_KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>

          {rateKind !== "by_quote" && (
            <Field label={rateKind === "hourly" ? "Hourly rate" : "Flat rate"} hint="USD. Leave blank if you'd rather discuss it in DMs.">
              <input className="input" value={rate} onChange={(e) => setRate(e.target.value)}
                placeholder="e.g. 20" />
            </Field>
          )}
        </div>

        <div className="card card--pad-lg">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 500 }}>Photos</div>
              <div className="text-xs text-mute">Up to {SV_MAX_IMAGES} images (optional).</div>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => fileInputRef.current?.click()}
              disabled={images.length + pendingFiles.length >= SV_MAX_IMAGES}>
              <Icon name="upload" size={14} />&nbsp; Add images
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={onFilesPicked} />
          </div>
          {(images.length > 0 || pendingFiles.length > 0) && (
            <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              {images.map((img) => (
                <div key={img.id} style={{ position: "relative", width: 88, height: 88, borderRadius: 8, overflow: "hidden", border: "1px solid var(--hairline)" }}>
                  <ServiceImage imageId={img.id} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <button type="button" onClick={() => onRemoveExisting(img.id)}
                    title="Remove image" aria-label="Remove image"
                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: 0, fontSize: 12, cursor: "pointer" }}>×</button>
                </div>
              ))}
              {pendingFiles.map((f) => (
                <div key={f.id} style={{ position: "relative", width: 88, height: 88, borderRadius: 8, overflow: "hidden", border: "1px dashed var(--accent)" }}>
                  <img src={f.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <button type="button" onClick={() => onRemovePending(f.id)}
                    title="Remove image" aria-label="Remove pending image"
                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: 0, fontSize: 12, cursor: "pointer" }}>×</button>
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, textAlign: "center", padding: "2px 0" }}>will upload</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Post offer")}
          </button>
        </div>
      </form>
    </div>
  );
}

export { Services };
